// One Experiments v2 test: headline numbers, then tabs in the order the common
// A/B tools use (Results with health checks and the scorecard, Players, Setup,
// QA overrides, History), plus lifecycle actions, economy approval and the
// final decision.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type {
  ExpV2ActionBody,
  ExpV2Counts,
  ExpV2Detail,
  ExpV2Override,
  ExpV2Population,
  ExpV2Results,
  ExpV2SubjectType,
  ExpV2Timeline,
} from '@/types/experimentsV2';
import { apiErrorMessage } from '@/utils/apiError';
import { AutoRefreshToggle } from '@/components/AutoRefreshToggle';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';
import { ActionBar } from './ActionBar';
import { DecisionForm } from './DecisionForm';
import { HealthChecks, RecommendationBanner } from './HealthChecks';
import { PlayersPanel } from './PlayersPanel';
import { Scorecard } from './Scorecard';
import { SummaryStrip } from './SummaryStrip';
import { healthChecks, recommendation } from './results';
import { OverridesPanel } from './OverridesPanel';
import { PendingApproval } from './PendingApproval';
import { StateLogTable } from './StateLogTable';
import {
  actionLabel,
  actionOutcome,
  samePending,
  type ActionAnswer,
  type ActionOption,
} from './actions';
import {
  STATUS_COLOR,
  STATUS_LABEL,
  describeGate,
  describePreRegistration,
  describeTargeting,
} from './display';
import { fromTest, isTestKey, preRegistrationMissing } from './draftForm';
import { formatPercent } from './units';
import { useExperimentsAccess } from './useExperimentsAccess';

interface AdminIdentity {
  id: string;
}

const when = (iso?: string | null) => (iso ? dayjs(iso).format('D MMM YYYY HH:mm') : '-');

function showOutcome(o: { kind: 'success' | 'pending' | 'error'; text: string }) {
  if (o.kind === 'success') message.success(o.text);
  else if (o.kind === 'pending') message.info(o.text);
  else message.error(o.text);
}

async function answerOf(call: () => Promise<{ status: number; data?: unknown }>) {
  try {
    const r = await call();
    return { status: r.status, data: r.data } as ActionAnswer;
  } catch (error) {
    return { error } as ActionAnswer;
  }
}

export const DetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { key: rawKey } = useParams<{ key: string }>();
  // Only a real test key reaches the API; anything else is refused before any call.
  const key = isTestKey(rawKey) ? rawKey : '';
  const { canEdit } = useExperimentsAccess();
  const { data: identity } = useGetIdentity<AdminIdentity>();

  const [test, setTest] = useState<ExpV2Detail | null>(null);
  const [counts, setCounts] = useState<ExpV2Counts | null>(null);
  const [countsError, setCountsError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<ExpV2Override[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshMs, setRefreshMs] = useState(60_000);
  const [population, setPopulation] = useState<ExpV2Population>('assigned');
  const [results, setResults] = useState<ExpV2Results | null>(null);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [timeline, setTimeline] = useState<ExpV2Timeline | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  const loadCounts = useCallback(async () => {
    setNow(new Date());
    try {
      const { data } = await adminApi.experimentsV2.counts(key);
      setCounts(data);
      setCountsError(null);
    } catch (e) {
      setCountsError(apiErrorMessage(e, 'Could not load the counts'));
    }
  }, [key]);

  /** Results and timeline are cached on the server (10 and 5 min), so reloading them is cheap. */
  // Only the newest results request may write: a slow cold "assigned" query
  // must not overwrite a later "exposed" answer (or an auto-refresh tick).
  const resultsRequest = useRef(0);
  const loadResults = useCallback(
    async (p: ExpV2Population) => {
      const id = ++resultsRequest.current;
      setResultsLoading(true);
      try {
        const { data } = await adminApi.experimentsV2.results(key, p);
        if (id !== resultsRequest.current) return;
        setResults(data);
        setResultsError(null);
      } catch (e) {
        if (id !== resultsRequest.current) return;
        setResults(null);
        setResultsError(apiErrorMessage(e, 'Could not load the results'));
      } finally {
        if (id === resultsRequest.current) setResultsLoading(false);
      }
    },
    [key]
  );

  const loadTimeline = useCallback(async () => {
    try {
      const { data } = await adminApi.experimentsV2.timeline(key);
      setTimeline(data);
      setTimelineError(null);
    } catch (e) {
      setTimelineError(apiErrorMessage(e, 'Could not load players over time'));
    }
  }, [key]);

  const load = useCallback(async () => {
    setError(null);
    if (!key) {
      setError(`${rawKey ?? ''} is not a test key`);
      setLoading(false);
      return;
    }
    try {
      const [t, o] = await Promise.all([
        adminApi.experimentsV2.get(key),
        adminApi.experimentsV2.listOverrides(key),
      ]);
      setTest(t.data);
      setOverrides(o.data);
      if (t.data.kind === 'experiment' && t.data.status !== 'draft') {
        void loadCounts();
        void loadTimeline();
      }
    } catch (e) {
      setError(apiErrorMessage(e, `Could not load ${key}`));
    } finally {
      setLoading(false);
    }
  }, [key, rawKey, loadCounts, loadTimeline]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const hasResults = test?.kind === 'experiment' && test.status !== 'draft';
  useEffect(() => {
    if (hasResults) void loadResults(population);
  }, [hasResults, population, loadResults]);

  const refreshLive = useCallback(async () => {
    await Promise.all([loadCounts(), loadTimeline(), loadResults(population)]);
  }, [loadCounts, loadTimeline, loadResults, population]);

  useAutoRefresh(refreshLive, autoRefresh && hasResults, refreshMs);

  const input = useMemo(
    () => (test ? { test, counts, results, timeline, now } : null),
    [test, counts, results, timeline, now]
  );
  const checks = useMemo(() => (input ? healthChecks(input) : []), [input]);
  const advice = useMemo(() => (input ? recommendation(input) : null), [input]);

  const run = async (o: ActionOption, body: ExpV2ActionBody) => {
    const answer = await answerOf(() => adminApi.experimentsV2.action(key, o.action, body));
    showOutcome(actionOutcome(answer, o.label, key));
    await load();
  };

  const decide = async (label: string, call: () => Promise<{ status: number }>) => {
    showOutcome(actionOutcome(await answerOf(call), label, key));
    await load();
  };

  /**
   * Approve or reject only the request the admin saw: a replaced one is shown again first, and the
   * server refuses (409) one replaced in the same instant, because the call names the shown request.
   */
  const answerPending = async (
    label: string,
    call: (requestedAt: string) => Promise<{ status: number }>
  ) => {
    const shown = test?.pendingAction;
    if (!shown) return;
    try {
      const { data } = await adminApi.experimentsV2.get(key);
      if (!samePending(shown, data.pendingAction)) {
        setTest(data);
        message.warning('The waiting request changed since this page loaded. Check it again.');
        return;
      }
    } catch (e) {
      message.error(apiErrorMessage(e, `Could not re-check ${key}`));
      return;
    }
    await decide(label, () => call(shown.requestedAt));
  };

  const addOverrides = async (subjectType: ExpV2SubjectType, ids: string[], variant: string) => {
    try {
      const { data } = await adminApi.experimentsV2.addOverrides(key, {
        subjectType,
        subjectIds: ids,
        variant,
      });
      setOverrides(data.overrides);
      return data;
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not add the overrides'));
      return undefined;
    }
  };

  const removeOverride = async (id: string) => {
    try {
      await adminApi.experimentsV2.removeOverride(key, id);
      setOverrides((list) => list.filter((o) => o.id !== id));
    } catch (e) {
      message.error(apiErrorMessage(e, 'Could not remove the override'));
    }
  };

  if (loading) return <Spin />;
  if (error || !test) return <Alert type="error" showIcon message={error ?? `${key} not found`} />;

  const isTest = test.kind === 'experiment';
  const preMissing = test.status === 'draft' ? preRegistrationMissing(fromTest(test)) : [];
  const pending = test.pendingAction;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/experiments-v2')}>
          All tests
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {test.name} <Typography.Text code>{test.key}</Typography.Text>
        </Typography.Title>
        <Tag color={STATUS_COLOR[test.status]}>{STATUS_LABEL[test.status]}</Tag>
        <Tag>{isTest ? 'A/B test' : 'Rollout flag'}</Tag>
        {test.economy && <Tag color="gold">Economy</Tag>}
        {test.status === 'draft' && canEdit && (
          <Button icon={<EditOutlined />} onClick={() => navigate(`/experiments-v2/${key}/edit`)}>
            Edit
          </Button>
        )}
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            void load();
            if (hasResults) void loadResults(population);
          }}
        >
          Reload
        </Button>
      </Space>

      <PendingApproval
        test={test}
        adminId={identity?.id}
        canEdit={canEdit}
        onApprove={() =>
          answerPending(`${actionLabel(pending?.action ?? '')} (approved)`, (at) =>
            adminApi.experimentsV2.approve(key, at)
          )
        }
        onReject={() =>
          answerPending(`Reject ${actionLabel(pending?.action ?? '')}`, (at) =>
            adminApi.experimentsV2.reject(key, at)
          )
        }
      />

      <Card size="small">
        <ActionBar test={test} canEdit={canEdit} preMissing={preMissing} onRun={run} />
        {!canEdit && (
          <Typography.Text type="secondary">
            You can view this test but not change it.
          </Typography.Text>
        )}
      </Card>

      {isTest && test.status !== 'draft' && (
        <SummaryStrip test={test} counts={counts} results={results} now={now} />
      )}

      {(test.status === 'ended' || test.status === 'rolled_out') && (
        <Card size="small" title="Decision">
          <DecisionForm
            test={test}
            canEdit={canEdit}
            onSave={(body) =>
              decide('Decision', () => adminApi.experimentsV2.setDecision(key, body))
            }
          />
        </Card>
      )}

      <Card size="small">
        <Tabs
          defaultActiveKey={isTest && test.status !== 'draft' ? 'results' : 'setup'}
          tabBarExtraContent={
            isTest && test.status !== 'draft' ? (
              <AutoRefreshToggle
                enabled={autoRefresh}
                onEnabledChange={setAutoRefresh}
                intervalMs={refreshMs}
                onIntervalChange={setRefreshMs}
              />
            ) : undefined
          }
          items={[
            ...(isTest && test.status !== 'draft'
              ? [
                  {
                    key: 'results',
                    label: 'Results',
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <RecommendationBanner value={advice} />
                        <HealthChecks checks={checks} />
                        <Scorecard
                          results={results}
                          loading={resultsLoading}
                          error={resultsError}
                          population={population}
                          onPopulation={setPopulation}
                        />
                      </Space>
                    ),
                  },
                  {
                    key: 'players',
                    label: 'Players',
                    children: (
                      <PlayersPanel
                        test={test}
                        counts={counts}
                        countsError={countsError}
                        timeline={timeline}
                        timelineError={timelineError}
                      />
                    ),
                  },
                ]
              : []),
            {
              key: 'setup',
              label: 'Setup',
              children: (
                <Card size="small">
                  <Descriptions size="small" column={{ xs: 1, md: 2 }}>
                    <Descriptions.Item label="Layer">{test.layerKey}</Descriptions.Item>
                    <Descriptions.Item label="Buckets">
                      {test.rangeStart === undefined || test.rangeStart === null
                        ? `not placed (${test.rangeSize} reserved at start)`
                        : `${test.rangeStart}-${test.rangeStart + test.rangeSize - 1}`}
                    </Descriptions.Item>
                    <Descriptions.Item label="Live">
                      {formatPercent(test.rolloutSize)} of {formatPercent(test.rangeSize)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Stable phase from">
                      {when(test.stablePhaseFrom)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Starts">{when(test.startsAt)}</Descriptions.Item>
                    <Descriptions.Item label="Ends">{when(test.endsAt)}</Descriptions.Item>
                    <Descriptions.Item label="Targeting">
                      {describeTargeting(test.targeting).join('; ')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Version gate">
                      {describeGate(test).join('; ')}
                    </Descriptions.Item>
                    {test.preRegistration && (
                      <Descriptions.Item label="Hypothesis" span={2}>
                        {describePreRegistration(test.preRegistration)}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                  <Table
                    size="small"
                    rowKey="key"
                    pagination={false}
                    dataSource={test.variants}
                    columns={[
                      { title: 'Variant', dataIndex: 'key' },
                      ...(isTest
                        ? [
                            {
                              title: 'Weight',
                              dataIndex: 'weight',
                              render: (w: number) => `${w / 10}%`,
                            },
                          ]
                        : []),
                      { title: 'State', dataIndex: 'state' },
                      {
                        title: 'Params',
                        dataIndex: 'params',
                        render: (p: Record<string, unknown>) => (
                          <Typography.Text code style={{ fontSize: 12 }}>
                            {JSON.stringify(p)}
                          </Typography.Text>
                        ),
                      },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: 'qa',
              label: 'QA overrides',
              children: (
                <OverridesPanel
                  test={test}
                  overrides={overrides}
                  canEdit={canEdit}
                  onAdd={addOverrides}
                  onRemove={removeOverride}
                />
              ),
            },
            {
              key: 'history',
              label: 'History',
              children: <StateLogTable rows={test.stateLog} />,
            },
          ]}
        />
      </Card>
    </Space>
  );
};
